import { Guid } from "client_legacy/ndaw/guid";
import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	NedapOnsLiveInformationResponseModel,
	NedapOnsLiveInformationRequestModel,
	getLiveInformation,
	NedapOnsMyScheduleResponseModel,
	MyPlanningLiveInformationResponseModel,
	NedapOnsConnectionInterests
} from "client/Api/Workspace/LiveInformation/NedapOns/nedapOnsLiveInformation";
import { ensureNotNil } from "client/Misc/utils";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";
import { parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";

interface NedapOnsLiveInformationProviderOptions {
	intervalInSeconds: number;
}

interface NedapOnsResourceId {
	connectionId: number;
	date: string;
}

interface InterestedNedapOnsParty extends InterestedParty<Guid, NedapOnsResourceId> {
	updateMyPlanning?(response: MyPlanningLiveInformationResponseModel): void;
	updateMySchedule?(response: NedapOnsMyScheduleResponseModel): void;
}

class NedapOnsLiveInformationProvider extends LiveInformationProvider<
	NedapOnsLiveInformationProviderOptions,
	Guid,
	NedapOnsResourceId,
	NedapOnsConnectionInterests,
	InterestedNedapOnsParty,
	NedapOnsLiveInformationResponseModel
> {

	constructor(options: NedapOnsLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[NedapOnsResourceId, NedapOnsConnectionInterests]>
	): Promise<ReadonlyArray<[NedapOnsResourceId, NedapOnsLiveInformationResponseModel]>> {
		const requests: NedapOnsLiveInformationRequestModel[] = entries.map(entry => this.convertResourceIdToRequest(entry[0], entry[1]));
		const responses: NedapOnsLiveInformationResponseModel[] = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<NedapOnsConnectionInterests, NedapOnsLiveInformationResponseModel>,
		response: [NedapOnsConnectionInterests, NedapOnsLiveInformationResponseModel]
	) {
		const [interests, model] = response;
		if (interests.myPlanning) {
			cacheItem.retrievedInterests.myPlanning = true;
			cacheItem.data.data.myPlanning = model.data.myPlanning;
		}
		if (interests.mySchedule) {
			cacheItem.retrievedInterests.mySchedule = true;
			cacheItem.data.data.mySchedule = model.data.mySchedule;
		}
	}

	protected informParty(
		party: InterestedNedapOnsParty,
		cacheItem: [NedapOnsConnectionInterests, NedapOnsLiveInformationResponseModel]
	) {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: NedapOnsConnectionInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.myPlanning && retrievedInterests.myPlanning) {
			if (party.updateMyPlanning) {
				party.updateMyPlanning(
					ensureNotNil(
						response.data.myPlanning
					)
				);
			}
		}

		if (interestedIn.mySchedule && retrievedInterests.mySchedule) {
			if (party.updateMySchedule) {
				party.updateMySchedule(
					ensureNotNil(
						response.data.mySchedule
					)
				);
			}
		}
	}

	protected override createResourceMap<TValue>(): Map<NedapOnsResourceId, TValue> {
		return new NormalizedMap<NedapOnsResourceId, TValue>(
			normalizeKey
		);
	}

	private convertResourceIdToRequest(
		resourceId: NedapOnsResourceId,
		interests: NedapOnsConnectionInterests
	): NedapOnsLiveInformationRequestModel {
		return {
			connectionId: resourceId.connectionId,
			date: resourceId.date,
			interests
		};
	}

	private convertResponseToResourceId(
		response: NedapOnsLiveInformationResponseModel
	): NedapOnsResourceId {
		return {
			connectionId: response.connectionId,
			date: response.date
		};
	}
}

function normalizeKey(key: NedapOnsResourceId): string {
	const dateOnly: DateOnly = ensureNotNil(
		parseDtoDateOnlyIgnoringOffset(key.date)
	);

	const normalizedKey: string = `connection_${key.connectionId}_${dateOnly.toISOString()}`;
	return normalizedKey;
}

export {
	NedapOnsLiveInformationProvider,
	type InterestedNedapOnsParty,
	type NedapOnsResourceId
};